import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../supabaseClient';

function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const response = await fetch('/.netlify/functions/admin-get-users', {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });

        if (!response.ok) throw new Error('Failed to fetch admin data');

        const data = await response.json();
        setStats(data.stats);
        // Get 5 most recent users
        setRecentUsers((data.users || []).slice(0, 5));
      } catch (err) {
        console.error('Dashboard fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user]);

  if (loading) return <p style={styles.loading}>Loading dashboard...</p>;
  if (error) return <p style={styles.error}>Error: {error}</p>;

  return (
    <div>
      <h1 style={styles.title}>Admin Dashboard</h1>
      <p style={styles.subtitle}>PASS Profile 3.0 — Overview</p>

      {/* Primary metrics */}
      <div style={styles.statsGrid}>
        <StatCard label="Total Users" value={stats?.total_users || 0} color="#0B3D2E" />
        <StatCard label="Assessments Completed" value={stats?.assessments_completed || 0} color="#2A9D8F" />
        <StatCard label="Reports Generated" value={stats?.reports_generated || 0} color="#0B3D2E" />
        <StatCard label="Completion Rate" value={`${stats?.conversion_rate || 0}%`} color="#2A9D8F" />
      </div>

      {/* Secondary metrics */}
      <div style={styles.secondaryGrid}>
        <div style={styles.sectionCard}>
          <h3 style={styles.sectionTitle}>Revenue Sources</h3>
          <div style={styles.metricRow}>
            <span style={styles.metricLabel}>Paid Customers</span>
            <span style={styles.metricValue}>{stats?.paid_users || 0}</span>
          </div>
          <div style={styles.metricRow}>
            <span style={styles.metricLabel}>Free (Admin-Created)</span>
            <span style={styles.metricValue}>{(stats?.free_users || 0) - (stats?.coupon_users || 0)}</span>
          </div>
          <div style={styles.metricRow}>
            <span style={styles.metricLabel}>Coupon Redemptions</span>
            <span style={styles.metricValue}>{stats?.coupon_users || 0}</span>
          </div>
          <div style={{ ...styles.metricRow, borderBottom: 'none', paddingBottom: 0 }}>
            <span style={{ ...styles.metricLabel, fontWeight: 600, color: '#0B3D2E' }}>Est. Revenue</span>
            <span style={{ ...styles.metricValue, fontWeight: 700, color: '#0B3D2E' }}>
              ${((stats?.paid_users || 0) * 49.99).toFixed(2)}
            </span>
          </div>
        </div>

        <div style={styles.sectionCard}>
          <h3 style={styles.sectionTitle}>Recent Activity</h3>
          <div style={styles.metricRow}>
            <span style={styles.metricLabel}>New Users (Last 7 Days)</span>
            <span style={styles.metricValue}>{stats?.users_last_7_days || 0}</span>
          </div>
          <div style={styles.metricRow}>
            <span style={styles.metricLabel}>New Users (Last 30 Days)</span>
            <span style={styles.metricValue}>{stats?.users_last_30_days || 0}</span>
          </div>
          <div style={styles.metricRow}>
            <span style={styles.metricLabel}>Active Coupons</span>
            <span style={styles.metricValue}>{stats?.coupons?.active || 0}</span>
          </div>
          <div style={{ ...styles.metricRow, borderBottom: 'none', paddingBottom: 0 }}>
            <span style={styles.metricLabel}>Total Coupon Redemptions</span>
            <span style={styles.metricValue}>{stats?.coupons?.redemptions || 0}</span>
          </div>
        </div>
      </div>

      {/* Recent users */}
      <div style={styles.sectionCard}>
        <h3 style={styles.sectionTitle}>Recent Users</h3>
        {recentUsers.length > 0 ? (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Assessment</th>
                <th style={styles.th}>Joined</th>
              </tr>
            </thead>
            <tbody>
              {recentUsers.map(u => (
                <tr key={u.id} style={styles.tableRow}>
                  <td style={styles.td}>{u.full_name || <span style={{ color: '#B0B8C4' }}>--</span>}</td>
                  <td style={styles.td}>{u.email}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.typeBadge,
                      backgroundColor: u.payment_status === 'free_admin' ? '#F5F5F5' : u.payment_status === 'coupon' ? '#FFF3E0' : '#E8F5E9',
                      color: u.payment_status === 'free_admin' ? '#757575' : u.payment_status === 'coupon' ? '#E65100' : '#2E7D32',
                    }}>
                      {u.payment_status === 'free_admin' ? 'Free' : u.payment_status === 'coupon' ? 'Coupon' : 'Paid'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.typeBadge,
                      backgroundColor: u.assessment_status === 'completed' ? '#E8F5E9' : u.assessment_status === 'in_progress' ? '#FFF3E0' : '#F5F5F5',
                      color: u.assessment_status === 'completed' ? '#2E7D32' : u.assessment_status === 'in_progress' ? '#E65100' : '#757575',
                    }}>
                      {(u.assessment_status || 'not started').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td style={{ ...styles.td, color: '#6B7A8F', fontSize: 13 }}>
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '--'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ color: '#6B7A8F', fontSize: 14 }}>No users yet.</p>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={styles.statCard}>
      <p style={{ ...styles.statValue, color }}>{value}</p>
      <p style={styles.statLabel}>{label}</p>
    </div>
  );
}

const styles = {
  title: { fontSize: 24, fontWeight: 700, color: '#0B3D2E', margin: '0 0 4px 0' },
  subtitle: { fontSize: 14, color: '#6B7A8F', margin: '0 0 28px 0' },
  loading: { color: '#6B7A8F', fontSize: 15 },
  error: { color: '#D32F2F', fontSize: 15 },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 16,
    marginBottom: 24,
  },
  statCard: {
    background: 'white',
    borderRadius: 10,
    padding: '20px 16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    border: '1px solid #E8ECF0',
    textAlign: 'center',
  },
  statValue: { fontSize: 32, fontWeight: 700, margin: '0 0 4px 0' },
  statLabel: { fontSize: 12, color: '#6B7A8F', margin: 0, fontWeight: 500 },
  secondaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 16,
    marginBottom: 24,
  },
  sectionCard: {
    background: 'white',
    borderRadius: 10,
    padding: 24,
    border: '1px solid #E8ECF0',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    marginBottom: 0,
  },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#0B3D2E', margin: '0 0 16px 0' },
  metricRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #F0F2F5',
  },
  metricLabel: { fontSize: 14, color: '#6B7A8F' },
  metricValue: { fontSize: 14, fontWeight: 600, color: '#333' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    fontSize: 11,
    fontWeight: 600,
    color: '#6B7A8F',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    borderBottom: '2px solid #E8ECF0',
  },
  tableRow: { borderBottom: '1px solid #F0F2F5' },
  td: { padding: '10px 12px', verticalAlign: 'middle' },
  typeBadge: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'capitalize',
  },
};

export default AdminDashboard;
